/**
 * AppHeader — modern, two-row sticky top bar.
 *
 * Mobile  (< md): row 1 = logo + location chip + cart icon
 *                  row 2 = full-width search input
 * Desktop (>= md): logo · location chip · search (centred, flex-grow) · nav links · cart · theme · profile
 *
 * Replaces the Bootstrap Navbar JSX that used to live inside Layout.tsx.
 * Keeps using the existing GlobalSearchBar component for the autocomplete
 * dropdown so we don't duplicate that logic.
 */
import React, { Suspense, lazy, useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import {
  ShoppingCart,
  Bell,
  Package,
  ShoppingBag,
  UtensilsCrossed,
  Compass,
  Car,
  LogIn,
  LogOut,
  Menu as MenuIcon,
  User,
  Store,
  Settings,
  Truck,
  X,
} from 'lucide-react';
import { useAuth } from '../../hooks/useAuth';
import { useFeatureFlag } from '../../contexts/FeatureFlagContext';
import { useCartCount } from '../../hooks/useCartCount';
import Logo from '../Logo';
import GlobalSearchBar from '../GlobalSearchBar';
import ThemeToggle from '../ThemeToggle';
import LocationChip from './LocationChip';
import { Button, css } from '../../design';

// LocationSheet pulls in react-map-gl + mapbox-gl which is ~170 KB gzipped
// and initialises a WebGL context at module load. Lazy-load it so:
//   1. The initial bundle (and every server-rendered or jsdom test) stays light.
//   2. mapbox-gl never executes until the user actually opens the sheet.
const LocationSheet = lazy(() => import('./LocationSheet'));

const AppHeader: React.FC = () => {
  const { isAuthenticated, user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const storesEnabled = useFeatureFlag('stores');
  const catalogEnabled = useFeatureFlag('catalog');
  const foodEnabled = useFeatureFlag('food');
  const ridesEnabled = useFeatureFlag('rides');
  const { count: cartCount, total: cartTotal } = useCartCount();

  const [locationOpen, setLocationOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const handleLogout = () => {
    logout();
    setMobileMenuOpen(false);
    navigate('/login');
  };

  const navLinks: Array<{ to: string; label: string; icon: React.ReactNode; show: boolean }> = [
    { to: '/stores',      label: 'Stores',      icon: <ShoppingBag size={16} />,    show: storesEnabled },
    { to: '/catalog',     label: 'Catalog',     icon: <Compass size={16} />,        show: catalogEnabled },
    { to: '/restaurants', label: 'Food',        icon: <UtensilsCrossed size={16} />,show: foodEnabled },
    { to: '/rides/auto',  label: 'Rides',       icon: <Car size={16} />,            show: ridesEnabled },
  ];

  return (
    <>
      <header
        className="pm-navbar"
        style={{
          position: 'sticky',
          top: 0,
          zIndex: 1030,
          background: css.color.surface,
          borderBottom: `1px solid ${css.color.border}`,
          boxShadow: css.shadow.xs,
        }}
      >
        {/* Row 1 — always visible. Brand · location · (desktop search) · actions */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 12,
            padding: '10px 16px',
            maxWidth: 1320,
            margin: '0 auto',
          }}
        >
          <Link
            to="/"
            aria-label="ProvisionMart home"
            style={{ display: 'inline-flex', alignItems: 'center', textDecoration: 'none', flexShrink: 0 }}
            title="ProvisionMart — Fresh groceries from your neighbourhood"
          >
            <Logo size={44} variant="horizontal" />
          </Link>

          {/* Location chip — visible on desktop in row 1 (mobile shows it
              alongside the cart in the row-2 strip below). */}
          <div className="pm-header-location-desktop" style={{ flexShrink: 0 }}>
            <LocationChip onClick={() => setLocationOpen(true)} />
          </div>

          {/* Desktop search — flex-grow centre */}
          <div className="pm-header-search-desktop" style={{ flex: 1, display: 'flex', justifyContent: 'center', minWidth: 0 }}>
            <div style={{ width: '100%', maxWidth: 560 }}>
              <GlobalSearchBar />
            </div>
          </div>

          {/* Desktop right cluster */}
          <nav
            className="pm-header-nav-desktop"
            aria-label="Primary"
            style={{ display: 'flex', alignItems: 'center', gap: 4 }}
          >
            {navLinks.filter((l) => l.show).map((l) => (
              <Link
                key={l.to}
                to={l.to}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 6,
                  padding: '8px 10px',
                  borderRadius: 10,
                  color: location.pathname.startsWith(l.to) ? css.color.primary : css.color.text2,
                  background: location.pathname.startsWith(l.to) ? css.color.primarySoft : 'transparent',
                  fontSize: 13,
                  fontWeight: 600,
                  textDecoration: 'none',
                  transition: 'background 120ms ease, color 120ms ease',
                }}
              >
                {l.icon}
                {l.label}
              </Link>
            ))}
            {isAuthenticated && user?.role === 'customer' && (
              <Link
                to="/orders"
                aria-label="My orders"
                title="My orders"
                style={iconBtnLink()}
              >
                <Package size={18} />
              </Link>
            )}
            {isAuthenticated && user?.role === 'customer' && (
              <Link
                to="/notifications"
                aria-label="Notifications"
                title="Notifications"
                style={iconBtnLink()}
              >
                <Bell size={18} />
              </Link>
            )}
            {isAuthenticated && user?.role === 'store' && (
              <Link to="/store-dashboard" style={navLinkStyle(location.pathname === '/store-dashboard')}>
                <Store size={16} /> Dashboard
              </Link>
            )}
            {isAuthenticated && user?.role === 'delivery_agent' && (
              <Link to="/deliveries" style={navLinkStyle(location.pathname.startsWith('/deliveries'))}>
                <Truck size={16} /> Deliveries
              </Link>
            )}
            {isAuthenticated && user?.role === 'admin' && (
              <Link to="/admin" style={navLinkStyle(location.pathname.startsWith('/admin'))}>
                <Settings size={16} /> Admin
              </Link>
            )}
            <ThemeToggle />
            {isAuthenticated ? (
              <>
                <Link
                  to="/profile"
                  aria-label="Profile"
                  title={`Signed in as ${user?.email}`}
                  style={iconBtnLink()}
                >
                  <User size={18} />
                </Link>
                <button
                  type="button"
                  onClick={handleLogout}
                  title="Log out"
                  aria-label="Log out"
                  style={{
                    ...iconBtnLink(),
                    background: 'transparent',
                    border: 'none',
                    color: css.color.danger,
                  }}
                >
                  <LogOut size={18} />
                </button>
              </>
            ) : (
              <Button as={Link} variant="primary" size="sm" {...({ to: '/login' } as any)} leadingIcon={<LogIn size={14} />}>
                Sign in
              </Button>
            )}
            {/* Desktop cart pill — always rendered, distinct from header icons */}
            <CartPill cartCount={cartCount} cartTotal={cartTotal} />
          </nav>

          {/* Mobile right cluster */}
          <div className="pm-header-actions-mobile" style={{ display: 'none', alignItems: 'center', gap: 6, marginLeft: 'auto' }}>
            <CartPill cartCount={cartCount} cartTotal={cartTotal} compact />
            <ThemeToggle />
            <button
              type="button"
              aria-label={mobileMenuOpen ? 'Close menu' : 'Open menu'}
              onClick={() => setMobileMenuOpen((v) => !v)}
              style={{
                background: 'transparent',
                border: `1px solid ${css.color.border}`,
                borderRadius: 999,
                width: 36,
                height: 36,
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: css.color.text,
                cursor: 'pointer',
              }}
            >
              {mobileMenuOpen ? <X size={18} /> : <MenuIcon size={18} />}
            </button>
          </div>
        </div>

        {/* Row 2 — mobile location chip + search */}
        <div
          className="pm-header-row2-mobile"
          style={{
            display: 'none',
            alignItems: 'center',
            gap: 10,
            padding: '0 12px 10px',
            maxWidth: 1320,
            margin: '0 auto',
          }}
        >
          <div style={{ flexShrink: 0 }}>
            <LocationChip onClick={() => setLocationOpen(true)} compact />
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <GlobalSearchBar placeholder="Search groceries, dishes & stores…" />
          </div>
        </div>

        {/* Mobile menu dropdown */}
        {mobileMenuOpen && (
          <div
            className="pm-header-menu-mobile"
            style={{
              position: 'absolute',
              top: '100%',
              left: 0,
              right: 0,
              background: css.color.surface,
              borderBottom: `1px solid ${css.color.border}`,
              boxShadow: css.shadow.md,
              padding: 12,
              display: 'none',
            }}
          >
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 8 }}>
              {navLinks.filter((l) => l.show).map((l) => (
                <Link
                  key={l.to}
                  to={l.to}
                  onClick={() => setMobileMenuOpen(false)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    padding: '12px 14px',
                    borderRadius: 12,
                    background: css.color.surface2,
                    color: css.color.text,
                    textDecoration: 'none',
                    fontWeight: 600,
                  }}
                >
                  {l.icon} {l.label}
                </Link>
              ))}
              {isAuthenticated && user?.role === 'customer' && (
                <>
                  <Link to="/orders" onClick={() => setMobileMenuOpen(false)} style={mobileItemStyle()}>
                    <Package size={16} /> My Orders
                  </Link>
                  <Link to="/notifications" onClick={() => setMobileMenuOpen(false)} style={mobileItemStyle()}>
                    <Bell size={16} /> Notifications
                  </Link>
                  <Link to="/profile" onClick={() => setMobileMenuOpen(false)} style={mobileItemStyle()}>
                    <User size={16} /> Profile
                  </Link>
                </>
              )}
              {isAuthenticated && user?.role === 'store' && (
                <Link to="/store-dashboard" onClick={() => setMobileMenuOpen(false)} style={mobileItemStyle()}>
                  <Store size={16} /> Dashboard
                </Link>
              )}
              {isAuthenticated && user?.role === 'admin' && (
                <Link to="/admin" onClick={() => setMobileMenuOpen(false)} style={mobileItemStyle()}>
                  <Settings size={16} /> Admin
                </Link>
              )}
            </div>
            <div style={{ marginTop: 10 }}>
              {isAuthenticated ? (
                <Button variant="secondary" fullWidth onClick={handleLogout} leadingIcon={<LogOut size={14} />}>
                  Log out
                </Button>
              ) : (
                <Button as={Link} variant="primary" fullWidth {...({ to: '/login' } as any)} leadingIcon={<LogIn size={14} />}>
                  Sign in
                </Button>
              )}
            </div>
          </div>
        )}
      </header>

      <Suspense fallback={null}>
        {locationOpen && (
          <LocationSheet open={locationOpen} onClose={() => setLocationOpen(false)} />
        )}
      </Suspense>

      <style>{`
        /* Responsive: swap row-1 right cluster between desktop / mobile */
        @media (max-width: 991.98px) {
          .pm-header-search-desktop,
          .pm-header-nav-desktop,
          .pm-header-location-desktop { display: none !important; }
          .pm-header-actions-mobile  { display: inline-flex !important; }
          .pm-header-row2-mobile     { display: flex !important; }
          .pm-header-menu-mobile     { display: block !important; }
        }
      `}</style>
    </>
  );
};

function navLinkStyle(active: boolean): React.CSSProperties {
  return {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    padding: '8px 10px',
    borderRadius: 10,
    color: active ? css.color.primary : css.color.text2,
    background: active ? css.color.primarySoft : 'transparent',
    fontSize: 13,
    fontWeight: 600,
    textDecoration: 'none',
  };
}

function iconBtnLink(): React.CSSProperties {
  return {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 36,
    height: 36,
    borderRadius: 999,
    color: css.color.text,
    background: 'transparent',
    border: `1px solid ${css.color.border}`,
    textDecoration: 'none',
    cursor: 'pointer',
  };
}

function mobileItemStyle(): React.CSSProperties {
  return {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    padding: '12px 14px',
    borderRadius: 12,
    background: css.color.surface2,
    color: css.color.text,
    textDecoration: 'none',
    fontWeight: 600,
  };
}

/** Brand cart pill — total + qty badge. */
const CartPill: React.FC<{ cartCount: number; cartTotal: number; compact?: boolean }> = ({
  cartCount,
  cartTotal,
  compact,
}) => {
  const navigate = useNavigate();
  if (compact) {
    return (
      <button
        type="button"
        onClick={() => navigate(cartCount > 0 ? '/cart' : '/cart')}
        aria-label={`Cart with ${cartCount} items`}
        style={{
          position: 'relative',
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: 36,
          height: 36,
          borderRadius: 999,
          background: cartCount > 0 ? css.color.primary : 'transparent',
          color: cartCount > 0 ? '#FFFFFF' : css.color.text,
          border: `1px solid ${cartCount > 0 ? css.color.primary : css.color.border}`,
          cursor: 'pointer',
        }}
      >
        <ShoppingCart size={18} />
        {cartCount > 0 && (
          <span
            aria-hidden
            style={{
              position: 'absolute',
              top: -4,
              right: -4,
              minWidth: 18,
              height: 18,
              padding: '0 4px',
              borderRadius: 999,
              background: css.color.accent,
              color: '#FFFFFF',
              fontSize: 10,
              fontWeight: 800,
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: '0 0 0 2px var(--pm-color-surface)',
            }}
          >
            {cartCount > 99 ? '99+' : cartCount}
          </span>
        )}
      </button>
    );
  }

  return (
    <Button
      variant={cartCount > 0 ? 'primary' : 'secondary'}
      size="sm"
      onClick={() => navigate('/cart')}
      leadingIcon={<ShoppingCart size={14} />}
      style={{ marginLeft: 4 }}
    >
      {cartCount > 0 ? (
        <>
          <span style={{ fontVariantNumeric: 'tabular-nums' }}>{cartCount}</span>
          <span style={{ margin: '0 6px', opacity: 0.7 }}>·</span>
          <span style={{ fontVariantNumeric: 'tabular-nums' }}>₹{Math.round(cartTotal)}</span>
        </>
      ) : (
        'Cart'
      )}
    </Button>
  );
};

export default AppHeader;

